'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CONTACT_EMAIL } from '@/lib/contact'
import { isActive, type NavItem } from '@/lib/nav'
import { cn } from '@/lib/utils'

/** Must match the clip-path duration below — the fold-up drives navigation. */
const FOLD_MS = 500

/**
 * The nav as a full-screen panel.
 *
 * It used to be a dropdown card under the bar: the seven links at 0.95rem in a
 * 48px-tall list, sharing the screen with whatever was behind it. A menu that
 * is the only thing on screen is a different object — it can give each
 * destination display-size type, and it stops the page underneath competing
 * for the same glance.
 *
 * ── WHY IT NAVIGATES ON THE WAY OUT ──────────────────────────────────────
 * A tap closes the panel FIRST and pushes the route when the fold finishes.
 * Navigating immediately unmounts the panel mid-animation, so the menu appears
 * to vanish rather than to close, and on a slow route the visitor is left
 * looking at the page they were already on with no menu and no feedback. The
 * pending href is held in a ref and fired by a timer rather than by
 * `transitionend`: a clip-path transition does not reliably fire that event on
 * every engine, and a menu that swallowed the tap would be worse than one that
 * closes a frame early.
 *
 * ── WHY IT LIVES INSIDE THE HEADER ───────────────────────────────────────
 * It looks like a case for a portal to `document.body`, and that was the first
 * attempt. It does not work here: the header is nested in a `relative z-10`
 * page wrapper, so its own `z-40` is capped at 10 against anything at body
 * level — a portalled panel at any z-index that clears the page also clears
 * the header, and the close button goes with it. The result is a full-screen
 * menu, a scroll-locked page, and no visible way out.
 *
 * As a child of the header the panel shares the header's stacking context, so
 * ordering against the trigger is a plain z-index again. Nothing is clipped —
 * the header sets no `overflow` and no transform, so `fixed inset-0` still
 * resolves against the viewport.
 *
 * Scroll lock, Escape and a focus cycle are the price of `aria-modal`. The
 * trigger lives outside the portal and stays interactive, so it is stitched
 * into the tab cycle by hand at both ends.
 */
export function MobileNav({
  items,
  pathname,
  open,
  onClose,
  accountHref,
  accountLabel,
}: {
  items: NavItem[]
  pathname: string
  open: boolean
  /** Asks the header to flip `open` to false — the panel owns the timing. */
  onClose: () => void
  accountHref: string
  accountLabel: string
}) {
  const router = useRouter()
  const [mounted, setMounted] = useState(false)
  const [closing, setClosing] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [reduce, setReduce] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const pending = useRef<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setReduce(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  }, [])

  const finish = useCallback(() => {
    setMounted(false)
    setClosing(false)
    setRevealed(false)
    if (pending.current) {
      router.push(pending.current)
      pending.current = null
    }
  }, [router])

  // Dismissal has ONE path, and it starts by asking the header to flip `open`.
  // A close can arrive from either side — a link or Escape in here, or the
  // trigger in the bar, which owns the flag and knows nothing about this
  // component's animation — so anything that folded the panel locally would
  // simply not run for half the ways out of it. This records where to go and
  // hands over; the effect below does the folding for both.
  const leave = useCallback(
    (href?: string) => {
      pending.current = href ?? null
      onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (open || !mounted || closing) return
    setRevealed(false)
    setClosing(true)
    if (timer.current) clearTimeout(timer.current)
    if (reduce) finish()
    else timer.current = setTimeout(finish, FOLD_MS)
  }, [open, mounted, closing, reduce, finish])

  // Mount on open; the unmount is owned by `finish` so the fold can play out.
  // No reveal step: the unfold is a keyframe animation that starts as soon as
  // the element exists, so there is no "from" frame to wait for.
  useEffect(() => {
    if (!open) return
    if (timer.current) clearTimeout(timer.current)
    pending.current = null
    setClosing(false)
    setRevealed(false)
    setMounted(true)
    // See `.flou-menu-shown` in globals.css: the belt to the animation's
    // braces, for the case where the animation never advances.
    const id = setTimeout(() => setRevealed(true), FOLD_MS)
    return () => clearTimeout(id)
  }, [open])

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), [])

  useEffect(() => {
    if (!mounted) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        leave()
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      document.removeEventListener('keydown', onKey)
    }
  }, [mounted, leave])

  if (!mounted) return null

  /** Stagger as data — the CSS reads it as `--d`. */
  const item = (index: number) => ({ '--d': `${120 + index * 45}ms` }) as React.CSSProperties

  return (
    <div
      ref={panelRef}
      id="mobile-nav"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      className={cn(
        // Under the bar's own controls (z-50), over everything else in the
        // header's context — so the way out stays exactly where the way in
        // was. The panel's top padding clears the pill.
        'fixed inset-0 z-30 flex flex-col overflow-y-auto bg-[var(--flou-void,#08080c)] lg:hidden',
        closing ? 'flou-menu-out' : 'flou-menu-in',
        revealed && !closing && 'flou-menu-shown',
      )}
    >
      <nav
        aria-label="Menu"
        className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center px-5 pt-24 pb-10 sm:px-8"
      >
        <div className="flex flex-col">
          {items.map((entry, index) => {
            const active = isActive(pathname, entry.href)
            return (
              <Link
                key={entry.label}
                href={entry.href}
                onClick={(event) => {
                  event.preventDefault()
                  leave(entry.href)
                }}
                aria-current={active ? 'page' : undefined}
                style={item(index)}
                className={cn(
                  'flou-menu-item font-masthead flex items-center gap-3 border-b border-[var(--flou-line)] py-4 text-[1.75rem] font-semibold tracking-[-0.02em] transition-colors duration-200 sm:text-[2.25rem]',
                  active ? 'text-white' : 'text-white/70 hover:text-white',
                )}
              >
                {entry.label}
                {entry.highlight ? (
                  <span
                    aria-hidden="true"
                    className="size-2 rounded-full"
                    style={{ background: 'var(--flou-gradient)' }}
                  />
                ) : null}
              </Link>
            )
          })}
        </div>

        <div className="flou-menu-item mt-9 flex flex-col gap-6" style={item(items.length)}>
          <Link
            href={accountHref}
            onClick={(event) => {
              event.preventDefault()
              leave(accountHref)
            }}
            className="inline-flex h-12 w-fit items-center rounded-full px-7 text-[0.95rem] font-bold text-white"
            style={{ background: 'var(--flou-gradient-solid)' }}
          >
            {accountLabel}
          </Link>

          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-[0.95rem] text-white/55 transition-colors duration-200 hover:text-white"
          >
            {CONTACT_EMAIL}
          </a>
        </div>
      </nav>
    </div>
  )
}
